import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { useAuth } from "./useAuth";
import { apiClient } from "@/shared/lib/api/apiClient";
import i18n, { APP_LANGUAGE_KEY, isSupportedLanguage, type SupportedLanguage } from "@/i18n";
import { isI18nEnabled } from "@/shared/lib/config";

export type SidebarPreference = "expanded" | "collapsed";

const SIDEBAR_PREFERENCE_KEY = "sidebar-preference";

export interface UserPreferences {
  sidebarPreference: SidebarPreference;
}

export interface UseUserPreferencesReturn {
  preferences: UserPreferences;
  setSidebarPreference: (preference: SidebarPreference) => void;
  setLanguage: (language: SupportedLanguage) => void;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  sidebarPreference: "expanded",
};

function getStoredPreferences(): UserPreferences {
  try {
    const stored = localStorage.getItem(SIDEBAR_PREFERENCE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        sidebarPreference: parsed.sidebarPreference || DEFAULT_PREFERENCES.sidebarPreference,
      };
    }
  } catch {
    // fallback to defaults
  }
  return DEFAULT_PREFERENCES;
}

function savePreferencesLocally(preferences: UserPreferences): void {
  try {
    localStorage.setItem(SIDEBAR_PREFERENCE_KEY, JSON.stringify(preferences));
  } catch {
    // silently ignore save failures
  }
}

const UserPreferencesContext = createContext<UseUserPreferencesReturn | null>(null);

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  // Load from localStorage immediately on mount to avoid flash of default state
  const [preferences, setPreferences] = useState<UserPreferences>(getStoredPreferences);
  const { user } = useAuth();
  const [lastUserId, setLastUserId] = useState<string | undefined>(undefined);

  // Sync preferences when user changes (login/logout)
  useEffect(() => {
    const currentUserId = user?.id;

    // User logged in (or changed user)
    if (currentUserId && currentUserId !== lastUserId) {
      // Load preferences from server - if server has a preference, use it; otherwise keep local
      const serverPref = user.sidebarPreference as SidebarPreference | undefined;
      if (serverPref && (serverPref === "expanded" || serverPref === "collapsed")) {
        const serverPreferences: UserPreferences = { sidebarPreference: serverPref };
        setPreferences(serverPreferences);
        savePreferencesLocally(serverPreferences);
      }

      setLastUserId(currentUserId);
    }
    // User logged out
    else if (!currentUserId && lastUserId) {
      // Reload from localStorage (keeps the last saved preference)
      const localPrefs = getStoredPreferences();
      setPreferences(localPrefs);
      setLastUserId(undefined);
    }
  }, [user?.id, user?.sidebarPreference, lastUserId]);

  // Efeito próprio, sem o guard de lastUserId: o AuthContext hidrata primeiro o
  // usuário STALE do cache e só depois o getProfile fresco — o idioma precisa
  // reagir às DUAS passadas, senão a escolha recém-feita reverte no F5.
  useEffect(() => {
    if (!user?.id) return;
    // Com a flag desligada o idioma do perfil e IGNORADO, nao apagado: a escolha feita em
    // ambiente com i18n ligado sobrevive no servidor e volta a valer quando a feature sair,
    // sem obrigar o usuario a escolher de novo. Aplicar aqui traria a UI em outro idioma
    // numa tela que nao tem mais seletor para desfazer.
    if (!isI18nEnabled()) return;
    const serverLanguage = user.preferredLanguage;
    if (isSupportedLanguage(serverLanguage) && serverLanguage !== i18n.language) {
      void i18n.changeLanguage(serverLanguage);
      try {
        localStorage.setItem(APP_LANGUAGE_KEY, serverLanguage);
      } catch {
        // storage indisponível — idioma vale só para a sessão
      }
    }
  }, [user?.id, user?.preferredLanguage]);

  const setSidebarPreference = async (preference: SidebarPreference) => {
    const newPreferences = { ...preferences, sidebarPreference: preference };
    setPreferences(newPreferences);
    savePreferencesLocally(newPreferences);

    // Save to server if user is logged in
    if (user?.id) {
      try {
        await apiClient.users.updatePreferences(user.id, { sidebarPreference: preference });
      } catch {
        // server sync is best-effort
      }
    }
  };

  const setLanguage = async (language: SupportedLanguage) => {
    // Guarda de ultimo recurso: com a flag desligada os dois pontos de entrada (LanguageMenu
    // e LanguageSelector) nem renderizam, mas quem chamar o hook direto nao deve conseguir
    // furar o pt-BR.
    if (!isI18nEnabled()) return;
    void i18n.changeLanguage(language);
    try {
      localStorage.setItem(APP_LANGUAGE_KEY, language);
    } catch {
      // storage indisponível — idioma vale só para a sessão
    }

    if (user?.id) {
      try {
        await apiClient.users.updatePreferences(user.id, { preferredLanguage: language });
      } catch {
        // sync com o servidor é best-effort
      }
    }
  };

  return (
    <UserPreferencesContext.Provider value={{ preferences, setSidebarPreference, setLanguage }}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences(): UseUserPreferencesReturn {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error("useUserPreferences must be used within UserPreferencesProvider");
  }
  return context;
}
